import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'

import { CostSavingChartData, DynamicPriceType, NullablePowerPrice, ThreeSixtySavings } from '../types'
import { ChartView, LoadingState } from 'src/Libs/chart-kit/shared/utils/chartModel'
import { Moment } from '@e3dc-react/shell/Libs/moment'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { fetchPowerPrices, fetchThreeSixtySavings } from '../api/power-price.api'
import { getTimePeriod, transformViewType } from '../utils'
import { requiresAdditionalDataPoints, updateFromToAddAdditionalDataPointToTheLeft } from '../utils/cost-saving-chart/one-tick-visualization'
import { fillDataWithEmptyTimestampValues, filterOutInvalidTimestamps, mergeCostSavingData } from '../utils/cost-saving-chart'
import { usePortalQueries } from 'src/Libs/api'
import { fillTimeValues } from 'src/Libs/system-data-handler/utils/chartFunctions'
import { useStorageListForMissingFarmData } from 'src/Libs/summary-data/hooks/use-storage-id-to-get-list'
import { deriveLoadingPercentage } from '../utils/cost-saving-chart/derive-loading-percentage'
import { useFeatureFlagsContext } from 'src/Libs/feature-flags/FeatureFlagsContext'
import { useWallboxHardwareByStorage } from 'src/Libs/wallboxes/hooks'
import { useFarmContext } from 'src/Elements/Farm'

/**
 * Hook to fetch the cost savings chart data. Based on the already existing and slow fetching method for system charts.
 *
 * TODO: Replace this hook with the new optimized route to fetch the data (history-values/steps)
 *
 * @param view
 * @param from
 * @param to
 * @returns
 */
export const useCostSavingsChartData = (
  view: ChartView,
  from?: Moment,
  to?: Moment,
  enabled?: boolean
): { data: CostSavingChartData[]; loadingState: LoadingState } => {
  const authFetch = useAuthFetchWithResponse()
  const { installationId, storageId } = useParams<{ installationId: string; storageId: string }>()
  const storagesList: string[] = useStorageListForMissingFarmData(storageId)
  const { isFarm } = useFarmContext()
  const viewTypeTransformed = transformViewType(view)
  const timePeriod = getTimePeriod(view)
  const doesRequireAdditionalDataPoint = requiresAdditionalDataPoints(view, to)
  const fetchFrom = useMemo(() => {
    return doesRequireAdditionalDataPoint && from ? updateFromToAddAdditionalDataPointToTheLeft(from, view) : from
  }, [doesRequireAdditionalDataPoint, from, view])
  const filledTimeValuesForCurrentView = useMemo(() => {
    if (from && to) {
      return fillTimeValues({ from, to, view: viewTypeTransformed })
    }
  }, [from, to, viewTypeTransformed])

  const { data: wallboxes } = useWallboxHardwareByStorage()
  const hasWallboxes = wallboxes.length > 0

  // Convert moment-dates to ISO strings for reliable query keys
  const toToIsoString = to?.toISOString()
  const fetchFromToIsoString = fetchFrom?.toISOString()
  const fromToIsoString = from?.toISOString()
  const {
    featureFlags: { isNewEmobiltyRouteEnabled },
    isFeatureFlagsLoading,
  } = useFeatureFlagsContext()
  const shouldUseNewRoute = !isFeatureFlagsLoading && isNewEmobiltyRouteEnabled

  const [powerPriceQuery, wallboxSavingsQuery, batterySavingsQuery] = usePortalQueries<
    NullablePowerPrice[] | ThreeSixtySavings[],
    NullablePowerPrice[] | ThreeSixtySavings[] | undefined
  >([
    {
      queryKey: ['power-prices', installationId, fetchFromToIsoString, toToIsoString],
      enabled: !!fetchFrom && !!to && !!enabled,
      queryFn: () => fetchPowerPrices(installationId, authFetch, fetchFrom, to, timePeriod),
      select: data => (data as NullablePowerPrice[] | null) ?? [],
    },
    {
      queryKey: ['three-sixty-wallbox-savings', storageId, fromToIsoString, toToIsoString, timePeriod, isFarm, shouldUseNewRoute],
      enabled: !!from && !!to && hasWallboxes && !!enabled,
      queryFn: () =>
        fetchThreeSixtySavings(DynamicPriceType.Wallbox, storageId, storagesList, isFarm, authFetch, shouldUseNewRoute, from, to, timePeriod),
      select: data => fillDataWithEmptyTimestampValues(data as ThreeSixtySavings[] | null, viewTypeTransformed, filledTimeValuesForCurrentView),
    },
    {
      queryKey: ['three-sixty-battery-savings', storageId, fromToIsoString, toToIsoString, timePeriod, isFarm, shouldUseNewRoute],
      enabled: !!from && !!to && !!enabled,
      queryFn: () =>
        fetchThreeSixtySavings(DynamicPriceType.Battery, storageId, storagesList, isFarm, authFetch, shouldUseNewRoute, from, to, timePeriod),
      select: data => fillDataWithEmptyTimestampValues(data as ThreeSixtySavings[] | null, viewTypeTransformed, filledTimeValuesForCurrentView),
    },
  ])
  const { data: powerPriceData, isLoading: isPowerPriceLoading } = powerPriceQuery
  const { data: threeSixtyWallboxData, isLoading: isWallboxLoading } = wallboxSavingsQuery
  const { data: threeSixtyBatteryData, isLoading: isBatteryLoading } = batterySavingsQuery

  const powerPriceDataInTimeRange = filterOutInvalidTimestamps((powerPriceData as NullablePowerPrice[] | undefined) ?? [], fetchFrom, to)

  const data: CostSavingChartData[] = mergeCostSavingData(
    powerPriceDataInTimeRange,
    threeSixtyBatteryData as ThreeSixtySavings[] | undefined,
    threeSixtyWallboxData as ThreeSixtySavings[] | undefined,
    fetchFrom,
    to,
    view
  )

  const { fetched, total } = deriveLoadingPercentage([isPowerPriceLoading, isWallboxLoading, isBatteryLoading])

  const isLoading = isPowerPriceLoading || isWallboxLoading || isBatteryLoading

  return { data: data, loadingState: { state: isLoading ? 0 : 1, fetchedMonths: fetched, monthAmountToFetch: total } }
}

/**
 * This hook uses the new optimized route to fetch the data (history-values/steps).
 * We cannot use it yet since it deliver "correct" data which would be different to PortalV2 and our other charts.
 * TODO: use this hook instead the one above
 *
 * @
 *
 * @param storageId
 * @param view
 * @param from
 * @param to
 * @returns
 */
// export const useChartDataFast = (storageId: string, view: ChartView, from?: number, to?: number): CostSavingChartData[] => {
//   const authFetch = useAuthFetchWithResponse()

//   const steps = getSteps(view)

//   const { data: historyData } = useQuery({
//     queryKey: ['history-data-dynamic-power-prices', storageId, from, to],
//     enabled: !!from && !!to,
//     queryFn: () => fetchHistoryStepData(storageId, from ?? 1, to ?? 1, steps, authFetch),
//   })

//   const data = historyData?.map(item => {
//     return {
//       time: item.time,
//       gridNetworkToHouseWithoutThreeSixtyEv: item.NetOut /** TODO: subtract three sixty ev charging */,
//       selfProduction: item.Consumption - item.NetOut,
//       threeSixtyEv: 0,
//       threeSixtyEvPrice: 0,
//       threeSixtyEvSavings: 0,
//       consumptionPrice: Math.random() * 10,
//     }
//   })

//   return data ?? []
// }
